import h from 'hyperscript';

import saveIcon from '@disy/cadenza-icons/save.svg';
import { logger } from '@disy/lib/logging';
import { icon } from '@disy/ui/icon';
import { showToastMessage } from '@disy/ui/toast-message';

import { errorAlert } from 'cadenza/alert';
import { callSaveAsCuratedMapView, replaceCuratedMapView } from 'cadenza/api-client/curated-map-views-api';
import { type RepositoryDto } from 'cadenza/api-client/dto/repository-dto';
import { isFeatureAvailable } from 'cadenza/features';
import { getStore } from 'cadenza/store/cadenza-store';
import { isObjecttypeBound } from 'cadenza/workbook/api/objecttype';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { isClassicLayer } from 'cadenza/workbook/map/workbook-layer-type-util';
import { refreshCuratedMapViews } from 'cadenza/workbook/state/default-map.state';
import { getIntegratedOverviewMap, isOverviewMap } from 'cadenza/workbook/state/workbook-map-view-selectors.state';
import type { SaveCuratedMapViewSettings } from 'cadenza/workbook/view/map/save-curated-map-view-dialog/save-curated-map-view-dialog';
import type { View } from 'cadenza/workbook/view/view';
import i18nView from 'cadenza/workbook/view/view.properties';
import {
  getObjecttypesByViewId,
  getViewById,
  getWorkbookNameAndHash,
  viewHasEffectiveSpatialFilter,
} from 'cadenza/workbook/workbook-core-selectors.state';

export function createSaveAsCuratedMapViewMenuItem(view: View, writableRepositories: RepositoryDto[]) {
  const isOverview = isOverviewMap(getStore().getState(), view.viewId);
  if (
    view.type === 'MAP' &&
    isFeatureAvailable('MANAGE_CURATED_MAP_VIEWS') &&
    writableRepositories.length &&
    !isOverviewMap(getStore().getState(), view.viewId)
  ) {
    return {
      icon: icon(saveIcon),
      label: i18nView('saveAsCuratedMapView.button.label'),
      disabled: isOverview,
      tooltip: isOverview ? i18nView('saveAsCuratedMapView.disabled.overviewMap.tooltip') : undefined,
      execute: () => {
        const state = getStore().getState();
        const integratedOverviewMap = getIntegratedOverviewMap(state, view.viewId);

        /// These checks are also done in the backend in CuratedMapViewDataViewLayerProcessor.java.
        /// So if they need to be adapted please make sure to keep both places consistent.

        const mainMapViewWithMetadata = getViewById(state, view.viewId);
        const mainMapHasClassicLayers = Object.values(mainMapViewWithMetadata.layerDefinitions).some(isClassicLayer);
        const overviewMapHasClassicLayers =
          integratedOverviewMap ? Object.values(integratedOverviewMap.layerDefinitions).some(isClassicLayer) : false;

        const mainMapHasBoundObjecttypes = getObjecttypesByViewId(state, view.viewId).some((ot) =>
          isObjecttypeBound(ot),
        );
        const overviewMapHasBoundObjecttypes =
          integratedOverviewMap ?
            getObjecttypesByViewId(state, integratedOverviewMap.id).some((ot) => isObjecttypeBound(ot))
          : false;

        const mainMapHasEffectiveSpatialFilter = viewHasEffectiveSpatialFilter(state, view.viewId);
        const overviewMapHasEffectiveSpatialFilter =
          integratedOverviewMap ? viewHasEffectiveSpatialFilter(state, integratedOverviewMap.id) : false;

        if (mainMapHasClassicLayers || overviewMapHasClassicLayers) {
          showCuratedMapViewErrorAlert('contains-classic-layers-or-template');
        } else if (mainMapHasBoundObjecttypes || overviewMapHasBoundObjecttypes) {
          showCuratedMapViewErrorAlert('curated-map-view-ot-bound-to-wb');
        } else if (mainMapHasEffectiveSpatialFilter || overviewMapHasEffectiveSpatialFilter) {
          showCuratedMapViewErrorAlert('curated-map-view-invalid-spatial-filter');
        } else {
          import('cadenza/workbook/view/map/save-curated-map-view-dialog/save-curated-map-view-dialog')
            .then(({ SaveCuratedMapViewDialog }) =>
              new SaveCuratedMapViewDialog(writableRepositories, view.name).connectAndShow(),
            )
            .then((settings) => settings && saveAsCuratedMapView(view.viewId, settings))
            .catch((error) => logger.error('Error encountered during import of save-curated-map-view-dialog.', error));
        }
      },
    };
  }
}

function saveAsCuratedMapView(
  viewId: ViewId,
  { repositoryName, printName, mapExtent, description, curatedMapToBeReplaced }: SaveCuratedMapViewSettings,
) {
  const data = {
    workbookNameAndHash: getWorkbookNameAndHash(getStore().getState()),
    viewId,
    printName,
    mapExtent,
    description,
  };
  const saveOperation =
    curatedMapToBeReplaced ?
      replaceCuratedMapView(curatedMapToBeReplaced, data)
    : callSaveAsCuratedMapView(repositoryName, data).then(() => getStore().dispatch(refreshCuratedMapViews()));
  saveOperation
    .then(() => showToastMessage(i18nView('saveAsCuratedMapView.success.toastMessage'), { type: 'success' }))
    .catch((response) => showCuratedMapViewErrorAlert(response?.key));
}

function showCuratedMapViewErrorAlert(errorKey?: string) {
  errorAlert(
    i18nView('saveAsCuratedMapView.error.title'),
    h(
      '.d-stack-v.space-1',
      i18nView('saveAsCuratedMapView.error.message'),
      (
        errorKey &&
          [
            'curated-map-view-ot-bound-to-wb',
            'curated-map-view-invalid-spatial-filter',
            'contains-classic-layers-or-template',
          ].includes(errorKey)
      ) ?
        h('span', i18nView(`saveAsCuratedMapView.error.<${errorKey}>`))
      : undefined,
    ),
  );
}
